import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";

import { Icon } from "./shared/icons";
import { buttonSx, g } from "./shared/tokens";

interface Props {
  title: string;
  estimate?: string;
  helper: string;
  buttonTitle: string;
  onClick: () => void;
  icon: JSX.Element;
  body?: JSX.Element;
  completed?: boolean;
  locked?: boolean;
  lockedReason?: string;
  onRefresh?: () => void;
  refreshLoading?: boolean;
}

export const OnboardingCard = ({
  title,
  estimate,
  helper,
  buttonTitle,
  onClick,
  icon,
  body = undefined,
  completed = false,
  locked = false,
  lockedReason,
  onRefresh,
  refreshLoading = false,
}: Props) => {
  return (
    <Box
      sx={{
        border: `1px solid ${completed ? g.greenBorder : g.border}`,
        background: completed ? g.green50 : locked ? g.surface2 : g.cardBg,
        borderRadius: g.radiusLg,
        p: "16px 18px",
      }}
    >
      <Box sx={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
        {/* Icon circle */}
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "999px",
            flexShrink: 0,
            display: "grid",
            placeItems: "center",
            mt: "2px",
            background: completed
              ? g.green100
              : locked
                ? "#ECEAE9"
                : g.primaryTint,
            color: completed
              ? g.successText
              : locked
                ? g.textDisabled
                : g.primary,
          }}
        >
          {completed ? (
            <Icon.Check size={18} />
          ) : locked ? (
            <Icon.Lock size={16} />
          ) : (
            icon
          )}
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                lineHeight: 1.3,
                color: completed ? g.text3 : locked ? g.textDisabled : g.text,
                textDecoration: completed ? "line-through" : "none",
              }}
            >
              {title}
            </Typography>

            {!completed && !locked && estimate && (
              <Box
                component="span"
                sx={{
                  px: "8px",
                  py: "2px",
                  borderRadius: "999px",
                  background: g.purpleBg,
                  color: g.purpleText,
                  fontSize: 11,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {estimate} min
              </Box>
            )}

            {locked && lockedReason && (
              <Typography
                sx={{ fontSize: 12, color: g.text3, fontWeight: 500 }}
              >
                · {lockedReason}
              </Typography>
            )}

            {onRefresh && !completed && !locked && (
              <Box sx={{ ml: "auto" }}>
                {refreshLoading ? (
                  <CircularProgress size={12} />
                ) : (
                  <IconButton size="small" onClick={onRefresh}>
                    <Box
                      component="i"
                      className="fa-regular fa-rotate-right"
                      sx={{ fontSize: 13, color: g.text3 }}
                    />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>

          {/* Helper text */}
          {!completed && (
            <Typography
              sx={{
                fontSize: 13,
                color: locked ? g.textDisabled : g.text2,
                mt: "4px",
                lineHeight: 1.45,
              }}
            >
              {helper}
            </Typography>
          )}

          {body}

          {/* CTA / completed marker */}
          {completed ? (
            <Typography
              sx={{
                color: g.successText,
                fontSize: 13,
                fontWeight: 600,
                mt: "2px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <Icon.Check size={14} /> Completed
            </Typography>
          ) : (
            !locked && (
              <Button
                sx={{ ...buttonSx.base, ...buttonSx.ghost, mt: "12px" }}
                onClick={onClick}
              >
                {buttonTitle} <Icon.ArrowRight size={14} />
              </Button>
            )
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default OnboardingCard;
